import React from "react";
import { Box, Container, Text, SimpleGrid, Flex, VStack } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { Shield, Brain, Target, Activity } from "lucide-react";

const MotionBox = motion(Box);

const outcomes = [
  {
    icon: Shield,
    title: "Build strong, reliable fundamentals",
    desc: "Deep subject knowledge that holds up under exam pressure and clinical practice.",
  },
  {
    icon: Brain,
    title: "Think more clearly through clinical concepts",
    desc: "Understand the 'why' behind every principle — not just the 'what'.",
  },
  {
    icon: Target,
    title: "Approach exams with confidence",
    desc: "Structured preparation that removes uncertainty and replaces it with clarity.",
  },
  {
    icon: Activity,
    title: "Step into wards with better clarity and readiness",
    desc: "Knowledge that bridges the gap between study and real patient care.",
  },
];

export const Outcome = () => {
  return (
    <Box bg="#E9FFFF" py={{ base: "64px", md: "96px" }}>
      <Container maxW="1200px" px={{ base: "5", md: "8" }}>

        <Flex direction="column" align="center" textAlign="center" mb={{ base: 12, md: 16 }}>
          <Text
            fontFamily="'Inter', sans-serif"
            fontSize="0.8125rem"
            fontWeight="600"
            letterSpacing="0.1em"
            textTransform="uppercase"
            color="#0095C8"
            mb={3}
          >
            Outcomes
          </Text>
          <Text
            as="h2"
            fontFamily="'Public Sans', sans-serif"
            fontWeight="600"
            fontSize={{ base: "1.75rem", md: "2.375rem" }}
            color="#293452"
            lineHeight="1.25"
            maxW="580px"
          >
            What changes when you{" "}
            <Box as="span" color="#0095C8">study this way</Box>
          </Text>
        </Flex>

        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={5}>
          {outcomes.map(({ icon: Icon, title, desc }, i) => (
            <MotionBox
              key={title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.1 }}
            >
              <VStack
                spacing={4}
                p={6}
                bg="white"
                borderRadius="12px"
                border="1px solid rgba(0,149,200,0.1)"
                boxShadow="0 2px 12px rgba(41,52,82,0.06)"
                h="full"
                align="start"
                _hover={{
                  transform: "translateY(-4px)",
                  boxShadow: "0 8px 32px rgba(41,52,82,0.12)",
                }}
                transition="all 0.3s cubic-bezier(0.4,0,0.2,1)"
                cursor="default"
              >
                <Box
                  w="48px"
                  h="48px"
                  borderRadius="12px"
                  background="linear-gradient(135deg, #0095C8, #016293)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="0 4px 12px rgba(0,149,200,0.25)"
                >
                  <Icon size={22} color="white" strokeWidth={1.75} />
                </Box>
                <Text
                  fontFamily="'Public Sans', sans-serif"
                  fontWeight="600"
                  fontSize="1rem"
                  color="#293452"
                  lineHeight="1.4"
                >
                  {title}
                </Text>
                <Text
                  fontFamily="'Inter', sans-serif"
                  fontSize="0.875rem"
                  color="rgba(41,52,82,0.6)"
                  lineHeight="1.7"
                >
                  {desc}
                </Text>
              </VStack>
            </MotionBox>
          ))}
        </SimpleGrid>

      </Container>
    </Box>
  );
};
